import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import { UncontrolledTooltip } from 'reactstrap'
import eventApi from '_api/event'
import { callNewListOrder, updateEventDeliveryCostInStore, updateEventDiscountInStore } from '_redux/modules/event'
import { clearDataPurchaseDetail, clearMyOrder } from '_redux/modules/order'
import {
  EVENT_CLOSE,
  NOT_PAYABLE,
  ORDER_DISCOUNT_CONDITION_MIN_SPEND,
  ORDER_DISCOUNT_TYPE_FIXED,
  PAYABLE,
  PAYNOW,
} from '_utils/constant'
import ModalRequestPayment from '../../../components/ModalRequestPayment'
import './style.scss'

export default function DiscountTicket({
  id,
  deliveryCost = 0,
  discount = 0,
  payable,
  status,
  eventAndOrderById,
  setTimeRender,
  paynow,
  isBuyerPaid = false,
  isPercentage = false,
  orderDiscountEnabled = false,
  isDiscountEnabled = false,
  orderDiscount,
  showRequestButton = true,
  style,
}) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()

  const [deliveryValue, setDeliveryValue] = useState(deliveryCost ?? 0)
  const [discountValue, setDiscountValue] = useState(discount ?? 0)
  const [modalRequest, setModalRequest] = useState(false)

  const toggleRequest = () => setModalRequest(!modalRequest)

  useEffect(() => {
    setDeliveryValue(deliveryCost ?? 0)
  }, [deliveryCost])

  useEffect(() => {
    setDiscountValue(discount ?? 0)
  }, [discount])

  const onDeliveryCostChange = (e) => {
    setDeliveryValue(e.target.value)
  }

  const onDeliveryCostUpdated = async (e) => {
    const newValue = Number(e.target.value)
    if (newValue === Number(deliveryCost)) {
      return
    }
    try {
      dispatch(clearDataPurchaseDetail())
      dispatch(clearMyOrder())
      await eventApi.updateEventDelivery(id, { deliveryCost: newValue })
      dispatch(updateEventDeliveryCostInStore(id, newValue))
      dispatch(callNewListOrder())
      if (setTimeRender) {
        setTimeRender((prevState) => prevState + 1)
      }
    } catch (error) {
      const { msgResp } = error
      addToast(msgResp || 'Failed to update delivery fee', { appearance: 'error', autoDismiss: true })
    }
  }

  const onDiscountChange = (e) => {
    setDiscountValue(e.target.value)
  }

  const onDiscountUpdated = async (e) => {
    const newValue = Number(e.target.value)
    if (newValue === Number(discount)) {
      return
    }
    try {
      dispatch(clearDataPurchaseDetail())
      dispatch(clearMyOrder())
      await eventApi.updateEventDiscount(id, { discount: newValue })
      dispatch(updateEventDiscountInStore(id, newValue))
      dispatch(callNewListOrder())
      if (setTimeRender) {
        setTimeRender((prevState) => prevState + 1)
      }
    } catch (error) {
      const { msgResp } = error
      addToast(msgResp || 'Failed to update discount', { appearance: 'error', autoDismiss: true })
    }
  }

  const onControlPaymentClicked = async () => {
    if (status === EVENT_CLOSE) {
      if (payable === NOT_PAYABLE) {
        toggleRequest()
      } else {
        try {
          dispatch(clearDataPurchaseDetail())
          dispatch(clearMyOrder())
          await eventApi.updateEventRequestPayment(id, {
            payable: payable === 1 ? 0 : 1,
          })
          dispatch(callNewListOrder())
          if (setTimeRender) {
            setTimeRender((prevState) => prevState + 1)
          }
        } catch (error) {
          const { msgResp } = error
          addToast(msgResp, { appearance: 'error', autoDismiss: true })
        }
      }
    } else {
      alert('Request payment when event is closed')
    }
  }

  const showTierDiscount = (orderDiscountEnabled || isDiscountEnabled) && Boolean(orderDiscount)
  const showPayLaterPanel = paynow !== PAYNOW && !orderDiscountEnabled

  if (!showTierDiscount && !showPayLaterPanel) {
    return null
  }

  const hasThreshold = Number(orderDiscount?.threshold) > 0
  const conditionText = hasThreshold
    ? (orderDiscount?.condition === ORDER_DISCOUNT_CONDITION_MIN_SPEND
      ? `Spend $${Number(orderDiscount.threshold).toFixed(2)}+`
      : `Order ${orderDiscount.threshold} + items`)
    : ''
  const discountText = orderDiscount?.type === ORDER_DISCOUNT_TYPE_FIXED
    ? `$${Number(orderDiscount?.amount).toFixed(2)} off`
    : `${orderDiscount?.amount}% off`

  const isRequested = payable === PAYABLE

  return (
    <div className="od-pl-wrapper" style={{ marginBottom: '1rem', ...style }}>
      {showTierDiscount ? (
        <div className="tier-list-container" style={{ marginBottom: '8px' }}>
          <div className="tier-item-manage-order">
            <div className="tier-info">
              <span className="tier-tag">🏷</span>
              <div className="tier-info-content" style={{ display: 'flex', flexDirection: 'column' }}>
                {conditionText ? <span className="tier-text">{conditionText}</span> : null}
                <span className="tier-text">Discount {discountText}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showPayLaterPanel && (
        <div className="od-pl-panel" id="od-pl-panel">
          <div className="od-pl-field">
            <span className="od-pl-field-label">Delivery fee</span>
            <span className="od-pl-unit" style={{ paddingLeft: '8px' }}>
              $
            </span>
            <input
              className="od-pl-input"
              id="od-pl-fee"
              type="number"
              value={deliveryValue}
              disabled={isBuyerPaid}
              style={{ paddingLeft: 0 }}
              onChange={onDeliveryCostChange}
              onBlur={onDeliveryCostUpdated}
            />
          </div>
          <div
            className="od-pl-field"
            id="od-pl-discount-container"
          >
            <span className="od-pl-field-label">Discount</span>
            <input
              className="od-pl-input"
              id="od-pl-discount"
              type="number"
              value={discountValue}
              disabled={isBuyerPaid}
              onChange={onDiscountChange}
              onBlur={onDiscountUpdated}
            />
            <span className="od-pl-unit">{isPercentage ? '%' : '$'}</span>
            {isBuyerPaid && (
              <UncontrolledTooltip placement="top" target="od-pl-discount-container" style={{ fontSize: '11px' }}>
                <span style={{ fontSize: '11px' }}>
                  The discount cannot be changed because the event already has paid orders.
                </span>
              </UncontrolledTooltip>
            )}
          </div>
          {showRequestButton && (
            <button
              className={`od-pl-request-btn ${isRequested ? 'sent' : ''}`}
              id="od-pl-btn"
              onClick={onControlPaymentClicked}
            >
              {isRequested ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
              {isRequested ? 'Payment Requested' : 'Request Payment'}
            </button>
          )}
        </div>
      )}

      {showPayLaterPanel && showRequestButton && eventAndOrderById && (
        <ModalRequestPayment
          modalRequest={modalRequest}
          toggleRequest={toggleRequest}
          eventAndOrderById={eventAndOrderById}
          setTimeRender={setTimeRender}
        />
      )}
    </div>
  )
}
